import React from "react";
import {
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  TalkDownAssignSite,
  TalkDownAssignment,
} from "./talkDownMessageTypes";

export const AssignSitesCamerasPicker = ({
  sites,
  search,
  onSearchChange,
  assignments,
  onChange,
  searchPlaceholder = "Search sites and cameras",
  camerasOnlySiteId,
}: {
  sites: TalkDownAssignSite[];
  search: string;
  onSearchChange: (value: string) => void;
  assignments: TalkDownAssignment[];
  onChange: (next: TalkDownAssignment[]) => void;
  searchPlaceholder?: string;
  /** When set, only that site's cameras are listed (no site checkboxes). */
  camerasOnlySiteId?: string | number | null;
}) => {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const camerasOnly = camerasOnlySiteId != null && camerasOnlySiteId !== "";
  const q = search.trim().toLowerCase();

  const filtered = sites.filter((site) => {
    if (
      camerasOnly &&
      String(site.siteId) !== String(camerasOnlySiteId)
    ) {
      return false;
    }
    if (!q) return true;
    if (site.siteName.toLowerCase().includes(q)) return true;
    return site.cameras.some((c) => c.cameraName.toLowerCase().includes(q));
  });

  const getAssignment = (siteId: string | number) =>
    assignments.find((a) => String(a.siteId) === String(siteId));

  const setSiteEntire = (site: TalkDownAssignSite, checked: boolean) => {
    const without = assignments.filter(
      (a) => String(a.siteId) !== String(site.siteId)
    );
    if (!checked) {
      onChange(without);
      return;
    }
    onChange([
      ...without,
      {
        siteId: site.siteId,
        siteName: site.siteName,
        mode: "entire_site",
        cameraIds: [],
        cameraNames: site.cameras.map((c) => c.cameraName),
      },
    ]);
  };

  const toggleCamera = (
    site: TalkDownAssignSite,
    cameraId: string | number,
    cameraName: string,
    checked: boolean
  ) => {
    const existing = getAssignment(site.siteId);
    const without = assignments.filter(
      (a) => String(a.siteId) !== String(site.siteId)
    );

    if (existing?.mode === "entire_site") {
      // Switching from entire site to per-camera: start with all except this one if unchecked
      const cameras = site.cameras.filter((c) =>
        checked
          ? true
          : String(c.cameraId) !== String(cameraId)
      );
      if (!cameras.length) {
        onChange(without);
        return;
      }
      if (cameras.length === site.cameras.length) {
        setSiteEntire(site, true);
        return;
      }
      onChange([
        ...without,
        {
          siteId: site.siteId,
          siteName: site.siteName,
          mode: "cameras",
          cameraIds: cameras.map((c) => c.cameraId),
          cameraNames: cameras.map((c) => c.cameraName),
        },
      ]);
      return;
    }

    const currentIds = existing?.cameraIds.map(String) ?? [];
    let nextIds = checked
      ? [...currentIds, String(cameraId)]
      : currentIds.filter((id) => id !== String(cameraId));
    nextIds = Array.from(new Set(nextIds));

    if (!nextIds.length) {
      onChange(without);
      return;
    }

    if (nextIds.length === site.cameras.length && site.cameras.length > 0) {
      setSiteEntire(site, true);
      return;
    }

    const nameById = new Map(
      site.cameras.map((c) => [String(c.cameraId), c.cameraName])
    );
    onChange([
      ...without,
      {
        siteId: site.siteId,
        siteName: site.siteName,
        mode: "cameras",
        cameraIds: nextIds,
        cameraNames: nextIds.map((id) => nameById.get(id) || cameraName),
      },
    ]);
  };

  const selectedSummary = () => {
    const siteN = assignments.length;
    const camN = assignments.reduce((n, a) => {
      if (a.mode === "entire_site") {
        return n + (a.cameraNames.length || 0);
      }
      return n + a.cameraIds.length;
    }, 0);
    return `SELECTED · ${siteN} SITE${siteN === 1 ? "" : "S"}, ${camN} CAMERA${
      camN === 1 ? "" : "S"
    }`;
  };

  const renderCameraCheckboxes = (
    site: TalkDownAssignSite,
    selectedCamIds: Set<string>,
    indent = false
  ) => (
    <div className={indent ? "pb-2 pl-10" : "px-3 py-2"}>
      {site.cameras.map((cam) => (
        <FormControlLabel
          key={String(cam.cameraId)}
          className="!m-0 block"
          control={
            <Checkbox
              size="small"
              checked={selectedCamIds.has(String(cam.cameraId))}
              onChange={(e) =>
                toggleCamera(
                  site,
                  cam.cameraId,
                  cam.cameraName,
                  e.target.checked
                )
              }
            />
          }
          label={
            <span className="text-[13px] text-actuate-blue">
              {cam.cameraName}
            </span>
          }
        />
      ))}
      {!site.cameras.length && (
        <p className="text-[13px] text-gray-500">No cameras at this site.</p>
      )}
    </div>
  );

  return (
    <div className="font-mulish">
      {!camerasOnly && (
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="mb-3 w-full rounded border border-actuate-grey px-3 py-2 text-[14px] text-actuate-blue"
        />
      )}
      <div className="max-h-[320px] overflow-y-auto rounded border border-actuate-grey">
        {filtered.map((site) => {
          const key = String(site.siteId);
          const assignment = getAssignment(site.siteId);
          const entire = assignment?.mode === "entire_site";
          const selectedCamIds = new Set(
            (assignment?.mode === "cameras"
              ? assignment.cameraIds
              : entire
              ? site.cameras.map((c) => c.cameraId)
              : []
            ).map(String)
          );
          const isOpen = expanded[key] ?? false;

          if (camerasOnly) {
            return (
              <div key={key}>
                {renderCameraCheckboxes(site, selectedCamIds)}
              </div>
            );
          }

          return (
            <div key={key} className="border-b border-actuate-grey last:border-b-0">
              <div className="flex items-center gap-1 px-2 py-1">
                <FormControlLabel
                  className="flex-1 !m-0"
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(assignment)}
                      indeterminate={
                        assignment?.mode === "cameras" &&
                        assignment.cameraIds.length > 0 &&
                        assignment.cameraIds.length < site.cameras.length
                      }
                      onChange={(e) => setSiteEntire(site, e.target.checked)}
                    />
                  }
                  label={
                    <span className="text-[14px] text-actuate-blue">
                      {site.siteName}
                      <span className="ml-2 text-[12px] text-gray-500">
                        {entire
                          ? `Entire site · ${site.cameras.length || "all"} cameras`
                          : `${site.cameras.length} cameras`}
                      </span>
                    </span>
                  }
                />
                {site.cameras.length > 0 && (
                  <IconButton
                    size="small"
                    aria-label={isOpen ? "Collapse cameras" : "Expand cameras"}
                    onClick={() =>
                      setExpanded((prev) => ({ ...prev, [key]: !isOpen }))
                    }
                  >
                    {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
              </div>
              <Collapse in={isOpen}>
                {renderCameraCheckboxes(site, selectedCamIds, true)}
              </Collapse>
            </div>
          );
        })}
        {!filtered.length && (
          <p className="p-4 text-[13px] text-gray-500">
            {camerasOnly ? "No cameras for this site." : "No sites match."}
          </p>
        )}
      </div>

      <p className="mt-4 text-[12px] font-bold uppercase tracking-wide text-actuate-blue">
        {selectedSummary()}
      </p>
    </div>
  );
};
